// @ts-ignore - snarkjs doesn't have type declarations
import * as snarkjs from "snarkjs";
import verificationKey from "./verification-key.json";

export interface ZkProof {
  pi_a: string[];
  pi_b: string[][];
  pi_c: string[];
  protocol: string;
  curve?: string;
}

export interface ProofData {
  proof: ZkProof;
  publicOutputs: string[];
}

export interface VerificationResult {
  valid: boolean;
  error?: string;
  emailAddress?: string;
  dinoHash?: string;
}

/**
 * Converts a field element to ASCII text.
 * Field elements represent ASCII text in reversed byte order.
 *
 * Example: fieldToAscii(2_018_721_414_038_404_820_327n) returns "gmail.com"
 */
function fieldToAscii(fieldValue: bigint): string {
  if (fieldValue === BigInt(0)) {
    return "";
  }

  let hexStr = fieldValue.toString(16);
  // Pad to even length
  if (hexStr.length % 2 !== 0) {
    hexStr = "0" + hexStr;
  }

  // Convert hex to bytes
  const bytes: number[] = [];
  for (let i = 0; i < hexStr.length; i += 2) {
    bytes.push(parseInt(hexStr.substring(i, i + 2), 16));
  }

  // Decode as ASCII and reverse
  const ascii = bytes
    .map((b) => String.fromCharCode(b))
    .join("")
    .replace(/\x00/g, "");

  return ascii.split("").reverse().join("");
}

/**
 * Converts multiple field elements to a single ASCII string.
 */
function fieldsToAscii(fields: bigint[]): string {
  return fields.map(fieldToAscii).join("");
}

/**
 * Public input field positions based on the circuit layout:
 * - Domain name: 9 fields (0-8)
 * - DKIM signer hash: 1 field (9)
 * - Email nullifier: 1 field (10)
 * - Timestamp: 1 field (11)
 * - Command text: 20 fields (12-31)
 * - Account salt: 1 field (32)
 * - Code embedded flag: 1 field (33)
 * - RSA public key modulus: 17 fields (34-50)
 * - Email address: 9 fields (51-59)
 */
const PUBLIC_INPUT_OFFSETS = {
  DOMAIN_START: 0,
  DOMAIN_LENGTH: 9,
  COMMAND_START: 12,
  COMMAND_LENGTH: 20,
  EMAIL_START: 51,
  EMAIL_LENGTH: 9,
} as const;

/**
 * Extracts the email address from public inputs.
 */
function extractEmailAddress(publicInputs: string[]): string {
  const emailFields = publicInputs
    .slice(
      PUBLIC_INPUT_OFFSETS.EMAIL_START,
      PUBLIC_INPUT_OFFSETS.EMAIL_START + PUBLIC_INPUT_OFFSETS.EMAIL_LENGTH,
    )
    .map((s) => BigInt(s));

  return fieldsToAscii(emailFields);
}

/**
 * Extracts the dino hash from the command text in public inputs.
 * Command format: "Sign DinoHash <hash>"
 */
function extractDinoHash(publicInputs: string[]): string {
  const commandFields = publicInputs
    .slice(
      PUBLIC_INPUT_OFFSETS.COMMAND_START,
      PUBLIC_INPUT_OFFSETS.COMMAND_START + PUBLIC_INPUT_OFFSETS.COMMAND_LENGTH,
    )
    .map((s) => BigInt(s));

  const commandText = fieldsToAscii(commandFields);

  // Extract the hash from "Sign DinoHash <hash>"
  const prefix = "Sign DinoHash ";
  if (commandText.startsWith(prefix)) {
    return commandText.substring(prefix.length).trim();
  }

  // Return the full command if format doesn't match
  return commandText;
}

export async function verifyZkProof(
  proof: ZkProof,
  publicInputs: string[],
): Promise<VerificationResult> {
  try {
    const isValid = await snarkjs.groth16.verify(
      verificationKey,
      publicInputs,
      proof,
    );

    if (isValid === true) {
      // Extract email address and dino hash from public inputs
      const emailAddress = extractEmailAddress(publicInputs);
      const dinoHash = extractDinoHash(publicInputs);

      return {
        valid: true,
        emailAddress,
        dinoHash,
      };
    }

    return {
      valid: false,
    };
  } catch (error) {
    return {
      valid: false,
      error:
        error instanceof Error ? error.message : "Unknown verification error",
    };
  }
}

export async function verifyProofData(
  proofData: ProofData,
): Promise<VerificationResult> {
  if (!proofData.proof || !proofData.publicOutputs) {
    return {
      valid: false,
      error: "Invalid proof data: missing proof or publicOutputs",
    };
  }

  return verifyZkProof(proofData.proof, proofData.publicOutputs);
}
